import {
  CloseOutlined,
  LoadingOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import { Alert, Button, Checkbox, Dropdown, Flex, Modal, Tooltip } from 'antd';
import React, { useRef, useState } from 'react';

import { AttachFileIcon, LinkIcon, StarIcon } from '@/assets/icons/icons';
import { CustomButton } from '@/components';

import AddLinkForm from '../../partials/AddLinkForm';
import styles from './EmailComposer.module.css';
import { useEmailReplyT } from './emailReplyStrings';
import PlaceholderEditor from './PlaceholderEditor';
import type { EmailComposerState } from './useEmailComposer';

interface Props {
  composer: EmailComposerState;
}

// The same file can be attached twice, so a file's name is no key and its
// position moves when another is removed. Each File object gets its own id.
const fileIds = new WeakMap<File, number>();
let nextFileId = 0;
const fileKey = (file: File): number => {
  let id = fileIds.get(file);
  if (id === undefined) {
    id = nextFileId++;
    fileIds.set(file, id);
  }
  return id;
};

const formatSize = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

// Send and Cancel, as in the Figma: 28 px high, 8 px radius, 12 px text.
const ACTION_BASE: React.CSSProperties = {
  height: 28,
  minWidth: 52,
  padding: '6px 8px',
  borderRadius: 8,
  fontSize: 12,
  lineHeight: '16px',
};
// Disabled Send is #f3f3f3 with #a6a6a6 text (the legacy disabled tokens carry
// those literals as their light-mode fallback).
const SEND_DISABLED: React.CSSProperties = {
  ...ACTION_BASE,
  fontWeight: 500,
  background: 'var(--zl-btn-disabled-bg, #f3f3f3)',
  borderColor: 'transparent',
  color: 'var(--zl-btn-disabled-text, #a6a6a6)',
};
// Enabled Send is black with white text.
const SEND_ENABLED: React.CSSProperties = {
  ...ACTION_BASE,
  fontWeight: 500,
  background: 'var(--text-primary)',
  borderColor: 'var(--text-primary)',
  color: 'var(--surface-container)',
};
// Cancel is plain 12 px semibold text with no background.
const CANCEL: React.CSSProperties = {
  ...ACTION_BASE,
  fontWeight: 600,
  background: 'transparent',
  border: 'none',
  color: 'var(--text-primary)',
};
// What a click on the box must leave alone: the real controls in it, and the
// text editor itself, which places the caret where the click lands.
const CONTROL_SELECTOR =
  'button, a, input, textarea, select, [contenteditable="true"], [role="button"], [role="menuitem"]';

/**
 * True when the click landed on the box itself: its padding or the empty space
 * beside and under its controls. Clicks on a control, and clicks that only
 * reach the box through a React portal (the Attach menu, dialogs), are not.
 */
const isEmptyArea = (event: React.MouseEvent<HTMLElement>): boolean => {
  const frame = event.currentTarget;
  const target = event.target as Element;
  if (!frame.contains(target)) return false;
  const control = target.closest(CONTROL_SELECTOR);
  return !control || !frame.contains(control);
};

/**
 * The reply box at the end of an email thread. Plain text only; recipients are
 * set by the server and never shown (PRD 4.3). All state is in the composer
 * hook, which sits outside the transcript so a reload cannot wipe it.
 */
const EmailComposer: React.FC<Props> = ({ composer }) => {
  const { t, fieldsNeedInput } = useEmailReplyT();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [linkVisible, setLinkVisible] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [documentsOpen, setDocumentsOpen] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);

  if (!composer.visible) return null;

  const { expanded, body, guardCount, canSend, outbox, attachmentNames } =
    composer;
  const sending = outbox.some((item) => item.status === 'sending');

  const attachErrorText =
    composer.attachError?.kind === 'limit'
      ? t('attachLimit', { mb: composer.attachError.mb })
      : composer.attachError?.kind === 'documents'
        ? t('documentsLoadFailed')
        : null;

  const handleCancel = () => {
    if (!composer.edited) {
      composer.collapse();
      return;
    }
    Modal.confirm({
      title: t('discardTitle'),
      okText: t('discard'),
      cancelText: t('keepEditing'),
      onOk: composer.discard,
    });
  };

  const openDocuments = () => {
    const _ = composer.loadDocuments();
    setPicked([]);
    setDocumentsOpen(true);
  };

  const notice = composer.conflict ? (
    <Alert
      type='warning'
      showIcon
      message={composer.conflict}
      style={{ marginBottom: 8 }}
    />
  ) : null;

  // "Draft with AI": right side of the first row, blue sparkle, 12 px, in the
  // project's blue token. Shown only while the box is empty.
  const draftWithAiButton = composer.canDraftWithAi ? (
    <Tooltip title={t('draftWithAiHint')}>
      <button
        type='button'
        onClick={composer.draftWithAi}
        className={styles.draftWithAi}
      >
        <StarIcon width={16} height={16} />
        {t('draftWithAi')}
      </button>
    </Tooltip>
  ) : null;

  // What the AI answer returned, when it was asked for and did not arrive.
  const aiNoticeLine =
    composer.aiNotice === 'failed' ? (
      <Flex
        align='center'
        gap={8}
        role='alert'
        data-testid='draft-ai-failed'
        style={{ fontSize: 12, color: 'var(--danger)', padding: '0 8px' }}
      >
        <span>{t('draftFailed')}</span>
        <Button
          type='link'
          size='small'
          style={{ fontSize: 12, padding: 0, height: 'auto' }}
          onClick={composer.draftWithAi}
        >
          {t('retry')}
        </Button>
      </Flex>
    ) : composer.aiNotice === 'none' ? (
      <div role='status' data-testid='draft-ai-none' className={styles.aiNone}>
        {t('draftNone')}
      </div>
    ) : null;

  // The whole box acts like its text: a click on an empty part of it opens the
  // box (resting) or puts the caret at the end of the text (open). One handler
  // on the frame; the controls inside keep doing only their own action. The
  // mouse-down is cancelled on the same parts so the text field does not lose
  // focus (and the highlight blink off) before the click lands.
  const keepFocus = (event: React.MouseEvent<HTMLElement>) => {
    if (isEmptyArea(event)) event.preventDefault();
  };
  const openFromEmptyArea = (event: React.MouseEvent<HTMLElement>) => {
    if (isEmptyArea(event)) composer.expand();
  };
  const focusFromEmptyArea = (event: React.MouseEvent<HTMLElement>) => {
    if (!isEmptyArea(event)) return;
    const field = composer.editorRef.current;
    if (!field) return;
    field.focus({ preventScroll: true });
    field.setSelectionRange(field.value.length, field.value.length);
  };

  const sendButton = (
    <Button
      size='small'
      onClick={() => composer.send()}
      disabled={!canSend || sending}
      style={canSend && !sending ? SEND_ENABLED : SEND_DISABLED}
    >
      {t('send')}
    </Button>
  );

  if (!expanded) {
    return (
      <div className={styles.root} data-testid='email-composer'>
        {notice}
        {/* The frame is a mouse target only; the "Message" button below is the
            keyboard way in, so there is no second tab stop. */}
        <div
          className={styles.frame}
          onMouseDown={keepFocus}
          onClick={openFromEmptyArea}
        >
          <Flex align='flex-start' justify='space-between' gap={8}>
            <button
              type='button'
              onClick={composer.expand}
              aria-label={t('message')}
              className={styles.messagePrompt}
            >
              {t('message')}
            </button>
            {draftWithAiButton}
          </Flex>
          {aiNoticeLine}
          <Flex justify='flex-end' style={{ marginTop: 'auto' }}>
            {sendButton}
          </Flex>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root} data-testid='email-composer'>
      {notice}

      <div
        className={styles.frame}
        onMouseDown={keepFocus}
        onClick={focusFromEmptyArea}
      >
        {/* Takes the frame's free height above the footer row, so opening the
            box does not make it taller than the resting 120 px. */}
        <Flex
          align='stretch'
          justify='space-between'
          gap={8}
          style={{ flex: 1 }}
        >
          <div className={styles.editor}>
            {composer.aiLoading && (
              <Flex
                align='center'
                gap={6}
                data-testid='draft-loading'
                style={{
                  fontSize: 12,
                  color: 'var(--text-light)',
                  marginBottom: 6,
                }}
              >
                <LoadingOutlined /> {t('drafting')}
              </Flex>
            )}
            <PlaceholderEditor
              value={body}
              onChange={composer.setBody}
              editorRef={composer.editorRef}
              placeholder={t('message')}
              ariaLabel={t('message')}
              disabled={false}
              autoFocus
            />
          </div>
          {draftWithAiButton}
        </Flex>

        {aiNoticeLine}

        {attachmentNames.length > 0 && (
          <div className={styles.attachments}>
            {composer.files.map((file, index) => (
              <Flex
                key={`file-${fileKey(file)}`}
                align='center'
                gap={6}
                style={{
                  fontSize: 12,
                  color: 'var(--text-light)',
                  marginBottom: 2,
                }}
              >
                <PaperClipOutlined />
                <span className={styles.fileName}>
                  {file.name} ({formatSize(file.size)})
                </span>
                <CloseOutlined
                  aria-label={t('removeAttachment', { name: file.name })}
                  role='button'
                  style={{ cursor: 'pointer', fontSize: 10 }}
                  onClick={() => composer.removeFile(index)}
                />
              </Flex>
            ))}
            {composer.documentIds.map((id) => {
              const name =
                composer.catalog.find((d) => d.id === id)?.file_name ??
                'Document';
              return (
                <Flex
                  key={`doc-${id}`}
                  align='center'
                  gap={6}
                  style={{
                    fontSize: 12,
                    color: 'var(--text-light)',
                    marginBottom: 2,
                  }}
                >
                  <PaperClipOutlined />
                  <span className={styles.fileName}>{name}</span>
                  <CloseOutlined
                    aria-label={t('removeAttachment', { name })}
                    role='button'
                    style={{ cursor: 'pointer', fontSize: 10 }}
                    onClick={() => composer.removeDocument(id)}
                  />
                </Flex>
              );
            })}
          </div>
        )}

        {attachErrorText && (
          <p role='alert' className={styles.attachError}>
            {attachErrorText}
          </p>
        )}

        {/* "3 fields still need input", inside the box above the footer. */}
        {guardCount !== null && (
          <div
            role='alert'
            data-testid='placeholder-guard'
            className={styles.guard}
          >
            <div>{fieldsNeedInput(guardCount)}</div>
            <Flex justify='flex-end' gap={12} style={{ marginTop: 8 }}>
              <CustomButton
                text={t('keepEditing')}
                onClick={composer.keepEditing}
                bgColor='var(--light-gray)'
                color='black'
                fontSize='12px'
                fontWeight='600'
                borderRadius='28px'
                height={28}
                minWidth='52px'
                padding='4px 8px'
                boxShadow='none'
              />
              <CustomButton
                text={t('sendAnyway')}
                onClick={composer.sendAnyway}
                bgColor='var(--light-gray)'
                color='black'
                fontSize='12px'
                fontWeight='600'
                borderRadius='28px'
                height={28}
                minWidth='52px'
                padding='4px 8px'
                boxShadow='none'
              />
            </Flex>
          </div>
        )}

        <input
          ref={fileInputRef}
          type='file'
          multiple
          data-testid='composer-file-input'
          className={styles.hidden}
          onChange={(event) => {
            composer.addFiles(Array.from(event.target.files ?? []));
            event.target.value = '';
          }}
        />

        <Flex
          justify='space-between'
          align='center'
          style={{ marginTop: 'auto', paddingTop: 8 }}
          gap={8}
        >
          <Flex align='center'>
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  { key: 'device', label: t('fromDevice') },
                  { key: 'documents', label: t('fromShipmentDocuments') },
                ],
                onClick: ({ key }) =>
                  key === 'device'
                    ? fileInputRef.current?.click()
                    : openDocuments(),
              }}
            >
              <button
                type='button'
                aria-label={t('attach')}
                className={styles.iconButton}
              >
                <AttachFileIcon />
              </button>
            </Dropdown>
            <button
              type='button'
              aria-label={t('insertLink')}
              className={styles.iconButton}
              onClick={() => setLinkVisible(true)}
            >
              <LinkIcon />
            </button>
          </Flex>
          <Flex gap={8} align='center'>
            <Button
              type='text'
              size='small'
              style={CANCEL}
              onClick={handleCancel}
            >
              {t('cancel')}
            </Button>
            {sendButton}
          </Flex>
        </Flex>
      </div>

      <AddLinkForm
        editorRef={composer.linkEditorRef}
        visible={linkVisible}
        setVisible={setLinkVisible}
        linkText={linkText}
        setLinkText={setLinkText}
      />

      <Modal
        open={documentsOpen}
        title={t('shipmentDocuments')}
        okText={t('attachSelected')}
        onCancel={() => setDocumentsOpen(false)}
        onOk={() => {
          composer.addDocuments(picked);
          setDocumentsOpen(false);
        }}
      >
        {composer.catalog.length === 0 ? (
          <p>{t('noDocuments')}</p>
        ) : (
          <Checkbox.Group
            value={picked}
            onChange={(values) => setPicked(values as string[])}
            style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
          >
            {composer.catalog.map((doc) => (
              <Checkbox key={doc.id} value={doc.id}>
                {doc.file_name} ({formatSize(doc.size_bytes)})
              </Checkbox>
            ))}
          </Checkbox.Group>
        )}
      </Modal>
    </div>
  );
};

export default EmailComposer;
